import { Code } from '@connectrpc/connect'
import {
  DemandBreakdownMode,
  type DemandBreakdownModeRow,
  DemandBreakdownStatus,
} from '@ebalance/proto/bffuser/v1/demand_breakdown_mode_model_pb'
import { type Control, Controller, type FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  type BoxProps,
  RadioGroupItem as EcRadioGroupItem,
  RadioGroupItemText as EcRadioGroupItemText,
  FieldRoot,
  HStack,
  Input,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
} from '~/components/common/Field'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { entitySourceOf, useEditModalForm } from '~/hooks/useEditModalForm'
import { useSaveDemandBreakdownMode } from '~/pages/DemandSettings/hooks/useDemandBreakdownModeMutations'
import { breakdownRetailerLabel } from '~/pages/DemandSettings/models/breakdownModeRows'
import {
  BreakdownModeFormSchema,
  type BreakdownModeFormValues,
} from '~/pages/DemandSettings/models/breakdownModeSchema'
import { resolveBreakdownSaveDetailedError } from '~/pages/DemandSettings/models/breakdownModeServerError'

// chakra v3.29 + React 18 types の既知不具合で `RadioGroupItemProps` /
// `RadioGroupItemTextProps` が `{ unstyled }` のみに collapse し、children を含む全 props を
// JSX で受け取れなくなる。取りこぼした props を BoxProps で型付けし直す
// (`TradeSideRadioGroup` / `IndividualBidModal` と同じ回避)。
const RadioGroupItem = EcRadioGroupItem as React.FC<
  BoxProps & { value: string; disabled?: boolean }
>
const RadioGroupItemText = EcRadioGroupItemText as React.FC<BoxProps>

const DIRECT = String(DemandBreakdownMode.DIRECT)
const BY_GROUP = String(DemandBreakdownMode.BY_GROUP)

const SAVE_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.Aborted]: 'page:demandSettings.breakdownMode.serverError.aborted',
}

/** 今の設定の mode。グループ未作成でもモーダルでは設定どおり「グループ別」を出す。 */
function currentMode(row: DemandBreakdownModeRow): string {
  return row.status === DemandBreakdownStatus.BY_GROUP ||
    row.status === DemandBreakdownStatus.BY_GROUP_NO_GROUPS
    ? BY_GROUP
    : DIRECT
}

type Props = {
  /** 開いた時点の行 (一覧の state に控えた版)。設定済みなら楽観ロックの基準もこの版に固定する。 */
  row: DemandBreakdownModeRow
  onClose: () => void
}

function modeOf(values: BreakdownModeFormValues): DemandBreakdownMode {
  return values.mode === BY_GROUP
    ? DemandBreakdownMode.BY_GROUP
    : DemandBreakdownMode.DIRECT
}

function toFormValues(row: DemandBreakdownModeRow): BreakdownModeFormValues {
  return { mode: currentMode(row), note: row.note ?? '' }
}

/**
 * 想定モードの設定・編集モーダル (AC-2 / AC-3 / AC-7)。保存すると一覧を取り直す。
 * 「グループ別」を選べるかは backend の判定 (`byGroupSelectable`) をそのまま使う。
 *
 * 設定済みの行は `useEditModalForm` で開いた版を基準に保存する。設定の無い行は基準を
 * 持たない (`expected_updated_at` を送らない = まだ設定が無い前提で保存する) ため、
 * 新規作成と同じ `useCrudModalForm` で保存する。
 */
export const BreakdownModeEditModal = ({
  row,
  onClose,
}: Props): React.ReactElement =>
  row.updatedAt !== undefined ? (
    <EditSettingModal row={row} onClose={onClose} />
  ) : (
    <NewSettingModal row={row} onClose={onClose} />
  )

const EditSettingModal = ({ row, onClose }: Props): React.ReactElement => {
  const { mutateAsync, isPending } = useSaveDemandBreakdownMode()
  const form = useEditModalForm<
    DemandBreakdownModeRow,
    BreakdownModeFormValues
  >({
    schema: BreakdownModeFormSchema,
    defaultValues: toFormValues(row),
    source: entitySourceOf(
      { data: row, isFetchedAfterMount: true, isError: false },
      (r) => r,
    ),
    toFormValues,
    onSubmit: async (values, { expectedUpdatedAt }) => {
      await mutateAsync({
        areaCode: row.areaCode,
        retailerCode: row.retailerCode,
        mode: modeOf(values),
        note: values.note,
        expectedUpdatedAt,
      })
    },
    onClose,
    customErrorKeys: SAVE_ERROR_KEYS,
    resolveDetailedError: resolveBreakdownSaveDetailedError,
  })
  return (
    <BreakdownModeModalLayout
      row={row}
      control={form.control}
      errors={form.errors}
      handleClose={form.handleClose}
      handleSave={form.handleSave}
      isDirty={form.isDirty}
      isPending={isPending}
      loadStatus={form.loadStatus}
      serverError={form.serverError}
    />
  )
}

const NewSettingModal = ({ row, onClose }: Props): React.ReactElement => {
  const { mutateAsync, isPending } = useSaveDemandBreakdownMode()
  const form = useCrudModalForm<BreakdownModeFormValues>({
    schema: BreakdownModeFormSchema,
    defaultValues: toFormValues(row),
    onSubmit: async (values) => {
      await mutateAsync({
        areaCode: row.areaCode,
        retailerCode: row.retailerCode,
        mode: modeOf(values),
        note: values.note,
      })
    },
    onClose,
    customErrorKeys: SAVE_ERROR_KEYS,
    resolveDetailedError: resolveBreakdownSaveDetailedError,
  })
  return (
    <BreakdownModeModalLayout
      row={row}
      control={form.control}
      errors={form.errors}
      handleClose={form.handleClose}
      handleSave={form.handleSave}
      isDirty={form.isDirty}
      isPending={isPending}
      serverError={form.serverError}
    />
  )
}

type LayoutProps = {
  row: DemandBreakdownModeRow
  control: Control<BreakdownModeFormValues>
  errors: FieldErrors<BreakdownModeFormValues>
  serverError: string | undefined
  handleClose: () => void
  handleSave: () => void
  isDirty: boolean
  isPending: boolean
  loadStatus?: 'idle' | 'loading' | 'failed' | 'ready'
}

const BreakdownModeModalLayout = ({
  row,
  control,
  errors,
  serverError,
  handleClose,
  handleSave,
  isDirty,
  isPending,
  loadStatus,
}: LayoutProps): React.ReactElement => {
  const { t } = useTranslation()
  const byGroupBlockedReason = row.byGroupSelectable
    ? undefined
    : t('page:demandSettings.breakdownMode.byGroupBlocked')

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen
      isPending={isPending}
      // 埋める元は一覧の state に控えた行なので取得の失敗は無い (loadStatus は常に ready)
      loadError={null}
      loadStatus={loadStatus}
      onClose={handleClose}
      serverError={serverError}
      size="md"
      title={t('page:demandSettings.breakdownMode.modalTitle', {
        retailer: breakdownRetailerLabel(row),
      })}
    >
      <Controller
        control={control}
        name="mode"
        render={({ field }) => (
          <FieldRoot>
            <FieldLabel id="breakdown-mode-label">
              {t('page:demandSettings.breakdownMode.modeLabel')}
            </FieldLabel>
            <RadioGroupRoot
              aria-labelledby="breakdown-mode-label"
              name={field.name}
              onValueChange={(details: { value: string | null }) => {
                if (details.value !== null) field.onChange(details.value)
              }}
              value={field.value}
            >
              <HStack gap={4}>
                <RadioGroupItem value={DIRECT}>
                  <RadioGroupItemHiddenInput />
                  <RadioGroupItemControl />
                  <RadioGroupItemText>
                    {t('page:demandSettings.breakdownMode.mode.direct')}
                  </RadioGroupItemText>
                </RadioGroupItem>
                <ButtonTooltip content={byGroupBlockedReason}>
                  <RadioGroupItem
                    disabled={byGroupBlockedReason !== undefined}
                    value={BY_GROUP}
                  >
                    <RadioGroupItemHiddenInput />
                    <RadioGroupItemControl />
                    <RadioGroupItemText
                      color={
                        byGroupBlockedReason !== undefined
                          ? 'Text.disabled'
                          : undefined
                      }
                    >
                      {t('page:demandSettings.breakdownMode.mode.byGroup')}
                    </RadioGroupItemText>
                  </RadioGroupItem>
                </ButtonTooltip>
              </HStack>
            </RadioGroupRoot>
            <FieldHelperText>
              {field.value === BY_GROUP
                ? t('page:demandSettings.breakdownMode.modeHint.byGroup')
                : t('page:demandSettings.breakdownMode.modeHint.direct')}
            </FieldHelperText>
          </FieldRoot>
        )}
      />
      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot alignItems="stretch" invalid={errors.note !== undefined}>
            <FieldLabel htmlFor="breakdown-note">
              {t('page:demandSettings.breakdownMode.note')}
            </FieldLabel>
            <Input
              {...field}
              id="breakdown-note"
              placeholder={t(
                'page:demandSettings.breakdownMode.notePlaceholder',
              )}
            />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />
    </CrudModalLayout>
  )
}
